<template>
  <span 
    :id="model?.system?.id"
    v-if="insightModalStore.initialItemsCount > 0">
    <SmartLinkItemComponent :id="model?.system?.id" class="content-block" :component="!model?.system?.workflow">
      <div class="container">
        <InsightModal />
      </div>
    </SmartLinkItemComponent>
  </span>
</template>

<script setup lang="ts">
import type { SearchResultInsightItem } from '~/types/SearchResultItem'
import type { InsightListPageModel, InsightPageModel } from '~/models'
import type { InsightSearchRequest } from '~/types/search-types/InsightSearchRequest'
import { useInsightModalStore } from '~/stores/modals/insight-modal'

const pageStore = usePageStore()
const siteStore = useSiteStore()
const pagerStore = usePaginationStore()
const loaderStore = useLoadingStore()
const route = useRoute()
const props = defineProps<{ model: InsightListPageModel }>()
let isSettingCategories = false
const availableCategoriesTemp = ref<string[]>([])
const insightModalStore = useInsightModalStore()
/// handling 'previous page (back)' btn
insightModalStore.resetStates()
insightModalStore.unMutableLayoutIsFullList =
  firstOrDefault(props.model?.elements.layout.value)?.codename.toLowerCase() === 'full_list'
insightModalStore.setLayoutIsFullList(insightModalStore.unMutableLayoutIsFullList)
const featuredItems =
  pageStore.getListAs<InsightPageModel>(props.model?.elements.featuredInsightItems.value) ??
  siteStore.getListAs<InsightPageModel>(props.model?.elements.featuredInsightItems.value)

insightModalStore.setSelectedCapability(firstOrDefault(props.model?.elements.relatedCapability.value)?.name || '')

async function fetchInsightPages(insightCategory?: string) {
  const body: InsightSearchRequest = {
    capability: insightModalStore.selectedCapability,
    author: insightModalStore.selectedAuthor,
    insightCategory: insightCategory || insightModalStore.selectedInsightCategory,
    searchValue: insightModalStore.searchValue || '',
    page: pagerStore.currentPage,
    size: insightModalStore.size,
    sort: insightModalStore.selectedSortValue
  }
  return await fetchInsightSearch(body)
}

async function handleDisplayOfItems() {
  if (insightModalStore.readMore == true) return
  insightModalStore.setLoading(true)
  insightModalStore.setItems([])
  insightModalStore.setFeaturedPreviewItems(featuredItems)
  const { data } = await fetchInsightPages()
  insightModalStore.setItems(data?.items as SearchResultInsightItem[])
  insightModalStore.setTotalResults(data?.totalCount || 0)
  pagerStore.setTotalPages(Math.ceil(insightModalStore.totalResults / insightModalStore.size))
  insightModalStore.setLoading(false)
}

async function getAuthors() {
  insightModalStore.setAuthors([])
  const people = await fetchPeople()
  const tempAuthors = [] as string[]
  people?.data?.items.map((p: any) => tempAuthors.push(p.elements.firstName.value + ' ' + p.elements.lastName.value))
  insightModalStore.setAuthors(tempAuthors)
}

async function fetchSelectOptions() {
  await getTaxonomies()
  await getAuthors()
}

async function getTaxonomies() {
  const taxonomy = await fetchTaxonomy()
  const availableCategories = taxonomy?.data
    ?.find((t: any) => t.system.codename === 'insight_category')
    ?.terms.map((t: any) => t.name)
  availableCategoriesTemp.value = availableCategories || []
}

async function readMore() {
  if (insightModalStore.readMore == false) return
  loaderStore.setLoading(true)
  pagerStore.changePage(pagerStore.currentPage + 1)
  const { data } = await fetchInsightPages()
  insightModalStore.addItems(data?.items as SearchResultInsightItem[])
  insightModalStore.setTotalResults(data?.totalCount || 0)
  loaderStore.setLoading(false)
  insightModalStore.setReadMore(false)
}

async function removeUnnecessaryFilters() {
  isSettingCategories = true;
  const availableCategories = [];

  for (const category of availableCategoriesTemp.value) {
    const pages = await fetchInsightPages(category);
    if ((pages.data?.totalCount && pages.data.totalCount > 0) || (route.query.i === category)) {
      availableCategories.push(category);
    }
  }

  insightModalStore.setSelectedInsightCategory('');
  insightModalStore.setAvailableCategories(availableCategories);
  isSettingCategories = false;
}

function getCategoriesByQuery(categoriesQuery: string): string {
  if (!categoriesQuery)
    return ''
  const queryCapabilities = insightModalStore.availableCategories.find((c: any) => c.toLowerCase() === categoriesQuery.toLowerCase())
  return queryCapabilities ?? ''
}

function getAuthorsByQuery(authorsQuery: string): string {
  if (!authorsQuery)
    return ''
  const queryAuthors = insightModalStore.authors.find((c: any) => c.toLowerCase() === authorsQuery.toLowerCase())
  return queryAuthors ?? ''
}
function getCapabilitiesByQuery(capabilitiesQuery: string): string {
  if (!capabilitiesQuery)
    return ''
  return capabilitiesQuery
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

watch(
  () => [
    insightModalStore.searchValue,
    insightModalStore.selectedAuthor,
    insightModalStore.selectedInsightCategory,
    insightModalStore.selectedSortValue,
    insightModalStore.layoutIsFullList,
    insightModalStore.openAsModal,
    insightModalStore.isGrid,
    insightModalStore.size,
    pagerStore.currentPage
  ],
  async () => {
    if (isSettingCategories) return // Skip the execution if setting categories
    await handleDisplayOfItems()
  }
)

watch(
  () => [insightModalStore.readMore],
  async () => {
    await readMore()
  }
)


onBeforeMount(async () => {
  if(pageStore.page?.system.type === 'people_page'){
    insightModalStore.setSelectedAuthor(pageStore.page?.elements.firstName.value + ' ' + pageStore.page?.elements.lastName.value)
    insightModalStore.setSelectedCapability('')
  }
  await fetchSelectOptions()
  await removeUnnecessaryFilters()
  if (route.query.c || route.query.a || route.query.i || route.query.s) {
    if (route.query.s) insightModalStore.setSearchValue(route.query.s as string)
    if (route.query.c) insightModalStore.setSelectedCapability(getCapabilitiesByQuery(route.query.c as string))
    if (route.query.a) insightModalStore.setSelectedAuthor(getAuthorsByQuery(route.query.a as string))
    if (route.query.i) insightModalStore.setSelectedInsightCategory(getCategoriesByQuery(route.query.i as string))
    await handleDisplayOfItems();
  }
  else {
    await handleDisplayOfItems()
  }
  insightModalStore.setInitialItemsCount(insightModalStore.items.length)
})

</script>
